import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../bootstrap/Button';
import Card, { CardBody, CardHeader, CardTitle } from '../../bootstrap/Card';
import FormGroup from '../../bootstrap/forms/FormGroup';
import Input from '../../bootstrap/forms/Input';
import Label from '../../bootstrap/forms/Label';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../bootstrap/Modal';
import Select from '../../bootstrap/forms/Select';
import Spinner from '../../bootstrap/Spinner';
import showNotification from '../../extras/showNotification';
import notificationBody from '../../../helpers/utils/functions/notificationBody';

const questionTypeOptions = [
  { value: 'rating', text: 'Avaliação de 1 a 5' },
  { value: 'boolean', text: 'Sim/Não' },
  { value: 'multiple', text: 'Opções' },
  { value: 'text', text: 'Texto' },
];

const booleanOptions = [
  { value: 'true', text: 'Sim' },
  { value: 'false', text: 'Não' },
];

const ratingDisplayOptions = [
  { value: 'numbers', text: 'Números' },
  { value: 'stars', text: 'Estrelas' },
];

const createEmptyOption = () => ({
  label: '',
  value: '',
});

const buildOptionValue = (label = '') =>
  label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '');

const createEmptyQuestion = () => ({
  type: '',
  label: '',
  required: 'true',
  rating_display: 'numbers',
  options: [createEmptyOption(), createEmptyOption()],
});

const buildFormStateFromQuestion = (questionSelected) => {
  if (!questionSelected) {
    return {
      name: '',
      active: 'true',
      questions: [createEmptyQuestion()],
    };
  }

  const orderedQuestions = [...(questionSelected.questions || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  return {
    name: questionSelected.name || '',
    active: String(Boolean(questionSelected.active)),
    questions:
      orderedQuestions.length > 0
        ? orderedQuestions.map((question) => ({
          type: question.type || '',
          label: question.label || '',
          required: String(Boolean(question.required)),
          rating_display: question.rating_display || 'numbers',
          options:
            question.type === 'multiple'
              ? (question.options || []).length > 0
                ? question.options.map((option) => ({
                  label: option.label || '',
                  value: option.value || '',
                }))
                : [createEmptyOption(), createEmptyOption()]
              : [],
        }))
        : [createEmptyQuestion()],
  };
};

const getApiMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  error?.code ||
  fallback;

const ModalRegisterQuestions = ({
  open,
  setIsOpen,
  setIsClose,
  estabId,
  questionSelected,
  onSubmit,
  onUpdate,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);
  const [form, setForm] = useState(buildFormStateFromQuestion());
  const isEditMode = Boolean(questionSelected?.id);

  const resetForm = useCallback(() => {
    setForm(buildFormStateFromQuestion());
    setSubmitCount(0);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      setForm(buildFormStateFromQuestion(questionSelected));
      setSubmitCount(0);
      setIsLoading(false);
    } else {
      resetForm();
    }
  }, [open, questionSelected, resetForm]);

  const handleClose = useCallback(() => {
    resetForm();
    setIsClose();
  }, [resetForm, setIsClose]);

  const setQuestionField = useCallback((index, field, value) => {
    setForm((current) => ({
      ...current,
      questions: current.questions.map((question, questionIndex) => {
        if (questionIndex !== index) return question;

        if (field === 'type') {
          return {
            ...question,
            type: value,
            rating_display: value === 'rating' ? question.rating_display || 'numbers' : 'numbers',
            options:
              value === 'multiple'
                ? question.options?.length > 1
                  ? question.options
                  : [createEmptyOption(), createEmptyOption()]
                : [],
          };
        }

        return {
          ...question,
          [field]: value,
        };
      }),
    }));
  }, []);

  const setOptionField = useCallback((questionIndex, optionIndex, field, value) => {
    setForm((current) => ({
      ...current,
      questions: current.questions.map((question, currentQuestionIndex) => {
        if (currentQuestionIndex !== questionIndex) return question;

        return {
          ...question,
          options: (question.options || []).map((option, currentOptionIndex) =>
            currentOptionIndex === optionIndex
              ? {
                ...option,
                ...(field === 'label'
                  ? {
                    label: value,
                    value: buildOptionValue(value),
                  }
                  : {
                    [field]: value,
                  }),
              }
              : option,
          ),
        };
      }),
    }));
  }, []);

  const addQuestion = useCallback(() => {
    setForm((current) => ({
      ...current,
      questions: [...current.questions, createEmptyQuestion()],
    }));
  }, []);

  const removeQuestion = useCallback((index) => {
    setForm((current) => ({
      ...current,
      questions: current.questions.filter((_, questionIndex) => questionIndex !== index),
    }));
  }, []);

  const moveQuestion = useCallback((index, direction) => {
    setForm((current) => {
      const targetIndex = index + direction;

      if (targetIndex < 0 || targetIndex >= current.questions.length) {
        return current;
      }

      const updatedQuestions = [...current.questions];
      const [selectedQuestion] = updatedQuestions.splice(index, 1);
      updatedQuestions.splice(targetIndex, 0, selectedQuestion);

      return {
        ...current,
        questions: updatedQuestions,
      };
    });
  }, []);

  const addOption = useCallback((questionIndex) => {
    setForm((current) => ({
      ...current,
      questions: current.questions.map((question, currentQuestionIndex) =>
        currentQuestionIndex === questionIndex
          ? {
            ...question,
            options: [...(question.options || []), createEmptyOption()],
          }
          : question,
      ),
    }));
  }, []);

  const removeOption = useCallback((questionIndex, optionIndex) => {
    setForm((current) => ({
      ...current,
      questions: current.questions.map((question, currentQuestionIndex) =>
        currentQuestionIndex === questionIndex
          ? {
            ...question,
            options: (question.options || []).filter((_, currentOptionIndex) => currentOptionIndex !== optionIndex),
          }
          : question,
      ),
    }));
  }, []);

  const errors = useMemo(() => {
    const validationErrors = {
      name: '',
      questions: [],
    };

    if (!form.name.trim()) {
      validationErrors.name = 'O nome do questionamento e obrigatorio.';
    }

    if (!form.questions.length) {
      validationErrors.questions.push({
        global: 'Adicione pelo menos uma questão.',
      });
      return validationErrors;
    }

    form.questions.forEach((question) => {
      const questionErrors = {};
      if (!question.type) {
        questionErrors.type = 'Selecione o tipo da questão.';
      }
      if (!question.label.trim()) {
        questionErrors.label = 'A pergunta e obrigatória.';
      }

      if (question.type === 'multiple') {
        const validOptions = (question.options || []).filter((option) => option.label.trim());

        if ((question.options || []).length < 2 || validOptions.length < 2) {
          questionErrors.options = 'Adicione ao menos duas opcoes validas.';
        }
      }

      validationErrors.questions.push(questionErrors);
    });

    return validationErrors;
  }, [form]);

  const hasErrors = useMemo(
    () =>
      Boolean(errors.name) ||
      errors.questions.some(
        (questionError) =>
          questionError?.global ||
          questionError?.type ||
          questionError?.label ||
          questionError?.options,
      ),
    [errors],
  );

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setSubmitCount((current) => current + 1);

    if (!estabId) {
      showNotification('', notificationBody('Selecione um estabelecimento para cadastrar.'), 'warning');
      return;
    }

    if (hasErrors) {
      showNotification('', notificationBody('Revise os campos obrigatorios do cadastro.'), 'warning');
      return;
    }

    const payload = {
      name: form.name.trim(),
      active: form.active === 'true',
      questions: form.questions.map((question, index) => {
        const payloadQuestion = {
          label: question.label.trim(),
          type: question.type,
          order: index + 1,
          required: question.required === 'true',
        };

        if (question.type === 'rating') {
          payloadQuestion.rating_display = question.rating_display || null;
        }

        if (question.type === 'multiple') {
          payloadQuestion.options = question.options
            .filter((option) => option.label.trim())
            .map((option) => ({
              label: option.label.trim(),
              value: buildOptionValue(option.label.trim()),
            }));
        }

        return payloadQuestion;
      }),
    };

    try {
      setIsLoading(true);
      if (isEditMode) {
        await onUpdate(questionSelected.id, payload);
        showNotification('', notificationBody('Questionamento atualizado com sucesso'), 'success');
      } else {
        await onSubmit({
          estabId,
          ...payload,
        });
        showNotification('', notificationBody('Questionamento cadastrado com sucesso'), 'success');
      }
      onSuccess && onSuccess();
      handleClose();
    } catch (error) {
      showNotification(
        '',
        notificationBody(
          getApiMessage(
            error,
            isEditMode
              ? 'Falha ao atualizar o questionamento'
              : 'Falha ao cadastrar o questionamento',
          ),
        ),
        'danger',
      );
    } finally {
      setIsLoading(false);
    }
  }, [estabId, form, handleClose, hasErrors, isEditMode, onSubmit, onSuccess, onUpdate, questionSelected]);

  return (
    <Modal
      id='modal-register-questionamentos'
      titleId='Cadastro de Questionamentos'
      isOpen={open}
      setIsOpen={setIsOpen}
      isStaticBackdrop={true}
      isScrollable={true}
      isCentered={true}
      size='xl'
      isAnimation={true}
    >
      <ModalHeader setIsOpen={handleClose}>
        <ModalTitle id='register-questionamentos'>
          {isEditMode ? 'Editar Questionamento' : 'Cadastro de Questionamentos'}
        </ModalTitle>
      </ModalHeader>
      <ModalBody className='p-5'>
        <form noValidate onSubmit={handleSubmit}>
          <div className='row'>
            <FormGroup id='questionamento-name' label='Nome' className='col-md-8 mb-4'>
              <Input
                id='questionamento-name'
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                isTouched={submitCount > 0}
                isValid={false}
                invalidFeedback={errors.name}
                placeholder='Digite o nome do questionamento'
              />
            </FormGroup>
            <FormGroup id='questionamento-active' label='Status' className='col-md-4 mb-4'>
              <Select
                id='questionamento-active'
                value={form.active}
                list={[
                  { value: 'true', text: 'Ativo' },
                  { value: 'false', text: 'Inativo' },
                ]}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    active: event.target.value,
                  }))
                }
              />
            </FormGroup>
          </div>

          <div className='d-flex justify-content-between align-items-center mb-4'>
            <h5 className='mb-0'>Questões</h5>
            <Button color='warning' icon='Add' onClick={addQuestion} type='button'>
              Adicionar questão
            </Button>
          </div>

          {errors.questions[0]?.global && submitCount > 0 && (
            <div className='alert alert-warning'>{errors.questions[0].global}</div>
          )}

          {form.questions.map((question, questionIndex) => (
            <Card key={`question-${questionIndex}`} className='mb-4'>
              <CardHeader className='d-flex justify-content-between align-items-center'>
                <CardTitle>Pergunta {questionIndex + 1}</CardTitle>
                <div className='d-flex gap-2'>
                  <Button
                    type='button'
                    color='dark'
                    icon='ArrowUpward'
                    size='sm'
                    isDisable={questionIndex === 0}
                    onClick={() => moveQuestion(questionIndex, -1)}
                  >
                    Subir
                  </Button>
                  <Button
                    type='button'
                    color='dark'
                    icon='ArrowDownward'
                    size='sm'
                    isDisable={questionIndex === form.questions.length - 1}
                    onClick={() => moveQuestion(questionIndex, 1)}
                  >
                    Descer
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <div className='row align-items-end'>
                  <FormGroup
                    id={`question-type-${questionIndex}`}
                    label='Tipo da questão'
                    className='col-md-3 mb-4'
                  >
                    <Select
                      id={`question-type-${questionIndex}`}
                      value={question.type}
                      placeholder='Selecione o tipo'
                      list={questionTypeOptions}
                      onChange={(event) =>
                        setQuestionField(questionIndex, 'type', event.target.value)
                      }
                      isTouched={submitCount > 0}
                      isValid={false}
                      invalidFeedback={errors.questions[questionIndex]?.type}
                    />
                  </FormGroup>

                  <FormGroup
                    id={`question-order-${questionIndex}`}
                    label='Ordem'
                    className='col-md-2 mb-4'
                  >
                    <Input
                      id={`question-order-${questionIndex}`}
                      value={questionIndex + 1}
                      readOnly
                    />
                  </FormGroup>

                  <FormGroup
                    id={`question-required-${questionIndex}`}
                    label='Obrigatória?'
                    className='col-md-2 mb-4'
                  >
                    <Select
                      id={`question-required-${questionIndex}`}
                      value={question.required}
                      list={booleanOptions}
                      onChange={(event) =>
                        setQuestionField(questionIndex, 'required', event.target.value)
                      }
                    />
                  </FormGroup>

                  {question.type === 'rating' && (
                    <FormGroup
                      id={`question-rating-display-${questionIndex}`}
                      label='Exibição da avaliação'
                      className='col-md-2 mb-4'
                    >
                      <Select
                        id={`question-rating-display-${questionIndex}`}
                        value={question.rating_display || 'numbers'}
                        list={ratingDisplayOptions}
                        onChange={(event) =>
                          setQuestionField(questionIndex, 'rating_display', event.target.value)
                        }
                      />
                    </FormGroup>
                  )}

                  <div className='col-md-auto mb-4 d-flex align-items-end justify-content-end ms-md-auto'>
                    <Button
                      type='button'
                      color='danger'
                      icon='Trash'
                      isDisable={form.questions.length === 1}
                      onClick={() => removeQuestion(questionIndex)}
                    >
                      Remover
                    </Button>
                  </div>

                  <FormGroup
                    id={`question-label-${questionIndex}`}
                    label='Pergunta'
                    className='col-md-12 mb-4'
                  >
                    <Input
                      id={`question-label-${questionIndex}`}
                      value={question.label}
                      onChange={(event) =>
                        setQuestionField(questionIndex, 'label', event.target.value)
                      }
                      isTouched={submitCount > 0}
                      isValid={false}
                      invalidFeedback={errors.questions[questionIndex]?.label}
                      placeholder='Digite o texto da pergunta'
                    />
                  </FormGroup>
                </div>

                {question.type === 'multiple' && (
                  <>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <Label className='mb-0'>Opções</Label>
                      <Button
                        type='button'
                        color='info'
                        icon='Add'
                        onClick={() => addOption(questionIndex)}
                        size='sm'
                      >
                        Adicionar opção
                      </Button>
                    </div>

                    {errors.questions[questionIndex]?.options && submitCount > 0 && (
                      <div className='text-danger small mb-3'>
                        {errors.questions[questionIndex].options}
                      </div>
                    )}

                    {(question.options || []).map((option, optionIndex) => (
                      <div className='row' key={`question-${questionIndex}-option-${optionIndex}`}>
                        <FormGroup
                          id={`option-label-${questionIndex}-${optionIndex}`}
                          label={`Opção ${optionIndex + 1}`}
                          className='col-md-10 mb-3'
                        >
                          <Input
                            id={`option-label-${questionIndex}-${optionIndex}`}
                            value={option.label}
                            onChange={(event) =>
                              setOptionField(questionIndex, optionIndex, 'label', event.target.value)
                            }
                            placeholder='Ex.: Atendimento'
                          />
                        </FormGroup>

                        <div className='col-md-2 mb-3 d-flex align-items-end'>
                          <Button
                            type='button'
                            color='danger'
                            icon='Trash'
                            size='sm'
                            isDisable={(question.options || []).length <= 2}
                            onClick={() => removeOption(questionIndex, optionIndex)}
                          >
                            Remover
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </CardBody>
            </Card>
          ))}

          <div className='d-flex justify-content-end gap-2 pt-3'>
            <Button
              color='danger'
              icon='cancel'
              rounded={1}
              onClick={handleClose}
              shadow='sm'
              hoverShadow='sm'
              size='sm'
              type='button'
            >
              Cancelar
            </Button>
            {isLoading ? (
              <Button color='success' rounded={1} hoverShadow='sm' shadow='sm' size='sm'>
                <Spinner color='light' inButton isGrow isSmall size={10} tag='span'>
                  Carregando...
                </Spinner>
                Salvando...
              </Button>
            ) : (
              <Button
                type='submit'
                color='success'
                icon='check'
                rounded={1}
                hoverShadow='sm'
                shadow='sm'
                size='sm'
              >
                {isEditMode ? 'Salvar alteracoes' : 'Confirmar'}
              </Button>
            )}
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ModalRegisterQuestions;
